import { useState, useRef, useCallback } from 'react'
import { useMembers } from '../../../hooks/useMembers'
import { cn } from '../../../lib/utils'
import { Portal } from '../../../components/Portal'

const CATEGORIES = ['medical','school','legal','insurance','financial','vehicle','other']

const ALLOWED_MIME = new Set([
  'image/jpeg','image/png','image/gif','image/webp','image/heic','image/heif',
  'image/tiff','image/bmp','image/svg+xml',
  'application/pdf','application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/epub+zip','application/x-mobipocket-ebook',
  'text/plain','text/csv','application/rtf',
])

function formatBytes(b) {
  if (b < 1024)         return `${b} B`
  if (b < 1048576)      return `${(b/1024).toFixed(1)} KB`
  if (b < 1073741824)   return `${(b/1048576).toFixed(1)} MB`
  return `${(b/1073741824).toFixed(1)} GB`
}

export const DocumentUploadModal = ({ onClose, onUpload, storageUsed = 0, isPaid = false }) => {
  const { data: members = [] } = useMembers()
  const inputRef = useRef(null)

  const [file,        setFile]        = useState(null)
  const [title,       setTitle]       = useState('')
  const [description, setDescription] = useState('')
  const [category,    setCategory]    = useState('other')
  const [memberId,    setMemberId]    = useState('')
  const [error,       setError]       = useState('')
  const [uploading,   setUploading]   = useState(false)
  const [progress,    setProgress]    = useState(0)
  const [dragOver,    setDragOver]    = useState(false)

  const storageLimit = isPaid ? 5368709120 : 104857600
  const storagePercent = Math.min(100, (storageUsed / storageLimit) * 100)
  const nearLimit = storagePercent > 80

  const handleFile = (f) => {
    setError('')
    if (!f) return
    if (!ALLOWED_MIME.has(f.type) && !f.name.match(/\.(pdf|docx?|xlsx?|pptx?|epub|mobi|txt|csv|rtf|jpe?g|png|gif|webp|heic|heif|tiff?|bmp|svg)$/i)) {
      setError('File type not allowed.')
      return
    }
    const maxSize = isPaid ? 26214400 : 5242880
    if (f.size > maxSize) {
      setError(`File too large. Max ${formatBytes(maxSize)} on your plan.`)
      return
    }
    if (storageUsed + f.size > storageLimit) {
      setError('Storage limit reached. Delete existing files or upgrade your plan.')
      return
    }
    setFile(f)
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''))
  }

  const onDrop = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files[0]
    if (f) handleFile(f)
  }, [isPaid, storageUsed, storageLimit, title])

  const handleSubmit = async () => {
    if (!file) return setError('Select a file to upload.')
    if (!title.trim()) return setError('Title is required.')
    setUploading(true)
    setError('')
    try {
      await onUpload({ file, title: title.trim(), description: description.trim(), category, memberId })
      onClose()
    } catch (e) {
      setError(e.code === 'STORAGE_LIMIT'
        ? 'Storage limit reached.'
        : e.message ?? 'Upload failed.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Portal>
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-4 overflow-hidden">
      <div className="bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg p-6 space-y-4 max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-text-primary text-lg">Upload document</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-secondary">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        {/* Storage usage */}
        <div className="text-xs text-text-muted">
          <div className="flex justify-between mb-1">
            <span>Storage: {formatBytes(storageUsed)} of {formatBytes(storageLimit)} used</span>
            {!isPaid && storagePercent > 80 && (
              <span className="text-amber-600 font-medium">Upgrade for 5 GB</span>
            )}
          </div>
          <div className="h-1 bg-border rounded-full overflow-hidden">
            <div
              className={cn('h-full rounded-full transition-all', storagePercent > 90 ? 'bg-red-500' : storagePercent > 70 ? 'bg-amber-400' : 'bg-green-500')}
              style={{ width: `${storagePercent}%` }}
            />
          </div>
        </div>

        {/* Drop zone */}
        <div
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onClick={() => inputRef.current?.click()}
          className={cn(
            'border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors',
            dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
          )}
        >
          <input
            ref={inputRef} type="file" className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          {file ? (
            <div className="text-sm">
              <p className="font-semibold text-text-primary">{file.name}</p>
              <p className="text-text-muted mt-1">{formatBytes(file.size)}</p>
            </div>
          ) : (
            <div className="text-sm text-text-muted">
              <svg className="w-8 h-8 mx-auto mb-2 text-border" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
              </svg>
              Drag a file here or <span className="text-primary">browse</span>
            </div>
          )}
        </div>

        {error && <p className="text-red-500 text-sm">{error}</p>}

        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title"
          className="w-full border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />

        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)"
          rows={2} className="w-full border border-border rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30" />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}
              className="w-full border border-border rounded-xl px-3 py-2.5 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30 capitalize">
              {CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-muted mb-1">Member (optional)</label>
            <select value={memberId} onChange={(e) => setMemberId(e.target.value)}
              className="w-full border border-border rounded-xl px-3 py-2.5 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30">
              <option value="">All members</option>
              {members.filter((m) => !m.isShared).map((m) => (
                <option key={m.user_id} value={m.user_id}>{m.profiles?.display_name}</option>
              ))}
            </select>
          </div>
        </div>

        {uploading && (
          <div className="h-1.5 bg-border rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full animate-pulse w-full" />
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button onClick={onClose} className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary">Cancel</button>
          <button onClick={handleSubmit} disabled={uploading || !file}
            className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50">
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  )
}
